import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api/api';
import PromotionsNearby from '../components/PromotionsNearby';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Star, MapPin, Clock, Certificate, Heart, Crown } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ProfessionalProfile = () => {
  const { id } = useParams();
  const [professional, setProfessional] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfessional();
    fetchReviews();
  }, [id]);

  const fetchProfessional = async () => {
    try {
      const response = await api.get(`/api/professionals/${id}`);
      setProfessional(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento del profilo');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await api.get(`/api/reviews/professional/${id}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!professional) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="text-center py-20">
          <p>Professionista non trovato</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Header */}
            <Card className="neumorphism rounded-2xl overflow-hidden" data-testid="professional-header">
              <div className="h-48 bg-muted relative">
                {professional.picture ? (
                  <img src={professional.picture} alt={professional.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-secondary/20">
                    <span className="text-7xl">🥼</span>
                  </div>
                )}
              </div>

              <div className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h1 className="font-heading font-semibold text-3xl">{professional.name}</h1>
                      {professional.is_premium && (
                        <div className="bg-accent text-white px-3 py-1 rounded-full flex items-center gap-1" data-testid="premium-badge">
                          <Crown size={14} weight="fill" />
                          <span className="text-xs font-medium">PREMIUM</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-foreground/70">
                      <div className="flex items-center gap-2">
                        <MapPin size={18} />
                        <span>{professional.location}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Star size={18} weight="fill" className="text-accent" />
                        <span className="font-medium">{professional.rating.toFixed(1)}</span>
                        <span>({professional.reviews_count})</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {professional.specializations.map((spec, idx) => (
                    <Badge key={idx} variant="secondary" className="bg-secondary text-secondary-foreground">
                      {spec}
                    </Badge>
                  ))}
                </div>

                <p className="text-foreground/80 leading-relaxed">{professional.bio}</p>
              </div>
            </Card>

            <PromotionsNearby
              professionalId={professional.professional_id}
              title="Promozioni attive di questo professionista"
              compact
            />

            {/* Certifications */}
            {professional.certifications && professional.certifications.length > 0 && (
              <Card className="neumorphism rounded-2xl p-8">
                <div className="flex items-center gap-2 mb-4">
                  <Certificate size={24} className="text-primary" weight="duotone" />
                  <h2 className="font-heading font-medium text-2xl">Certificazioni</h2>
                </div>
                <ul className="space-y-2">
                  {professional.certifications.map((cert, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{cert}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Reviews */}
            <Card className="neumorphism rounded-2xl p-8" data-testid="reviews-section">
              <h2 className="font-heading font-medium text-2xl mb-6">Recensioni</h2>
              {reviews.length === 0 ? (
                <p className="text-foreground/70">Nessuna recensione ancora</p>
              ) : (
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <div key={review.review_id} className="space-y-2" data-testid={`review-${review.review_id}`}>
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              size={16}
                              weight={i < review.rating ? 'fill' : 'regular'}
                              className={i < review.rating ? 'text-accent' : 'text-foreground/30'}
                            />
                          ))}
                        </div>
                        <span className="text-sm text-foreground/60">
                          {new Date(review.created_at).toLocaleDateString('it-IT')}
                        </span>
                      </div>
                      <p className="text-foreground/80">{review.comment}</p>
                      {review.reply && (
                        <div className="ml-4 mt-2 rounded-xl bg-[#F5FAFF] border-l-4 border-primary p-3" data-testid={`review-public-reply-${review.review_id}`}>
                          <div className="text-xs font-semibold text-primary mb-1">Risposta di {review.reply.author_name}</div>
                          <p className="text-sm text-foreground/80 whitespace-pre-line">{review.reply.text}</p>
                        </div>
                      )}
                      <Separator className="mt-4" />
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="neumorphism rounded-2xl p-6 sticky top-24" data-testid="booking-card">
              <div className="text-center mb-6">
                <div className="text-4xl font-heading font-bold text-foreground mb-2">
                  {professional.hourly_rate}€<span className="text-lg font-normal text-foreground/70">/ora</span>
                </div>
              </div>

              <Link to="/login" className="block">
                <Button className="w-full rounded-full mb-4" size="lg" data-testid="book-consultation-btn">
                  Prenota Consulenza
                </Button>
              </Link>

              <Separator className="my-6" />

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Clock size={24} className="text-accent flex-shrink-0" weight="duotone" />
                  <div>
                    <p className="font-medium mb-1">Risposta rapida</p>
                    <p className="text-sm text-foreground/70">Solitamente risponde entro 24 ore</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Certificate size={24} className="text-accent flex-shrink-0" weight="duotone" />
                  <div>
                    <p className="font-medium mb-1">Professionista Verificato</p>
                    <p className="text-sm text-foreground/70">Credenziali e certificazioni verificate</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Heart size={24} className="text-accent flex-shrink-0" weight="duotone" />
                  <div>
                    <p className="font-medium mb-1">Assistenza Dedicata</p>
                    <p className="text-sm text-foreground/70">Follow-up e supporto continuativo</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalProfile;